/**
 * Universal Product Selector behavior (USP)
 *
 * What this file does:
 * - Adds hover + click behavior for the universal selector rendered by `snippets/selector-product-default.liquid`.
 * - In `siblings` mode:
 *   - Hover swaps the info text to the hovered sibling's info (if an info row exists).
 *   - Click navigates to the sibling product page and tries to preserve the currently selected variant by matching
 *     option values by index (1..3). If no exact match exists, it navigates to the sibling without a variant param
 *     (Shopify default variant).
 * - In `variants` mode:
 *   - When an option has more than 5 values, a dropdown trigger opens a popup list.
 *   - The dropdown closes on outside click or option click, and the trigger text syncs to the selection.
 *
 * Depends on:
 * - `snippets/selector-product-default.liquid` output structure:
 *   - `fieldset[data-usp-mode]` wrapper
 *   - dropdown: `data-usp-dropdown`, `data-usp-dropdown-trigger`, `data-usp-dropdown-overlay`, `data-usp-dropdown-panel`
 *   - sibling buttons: `.usp__btn[data-usp-sibling]`
 *   - optional info container: `.usp__info > .usp__info-title`
 *   - JSON strings stored in attributes like `data-info-default` and `data-usp-info` (Liquid `| json`, HTML-escaped)
 *   - sibling dataset: `script[type="application/json"][data-usp-siblings]` inside the fieldset (siblings mode only)
 * - Dawn product section includes `variant-selects [data-selected-variant]` JSON (used to read current options).
 *
 * Intended behavior:
 * - Idempotent init: each fieldset initializes once via `data-usp-init="1"`.
 * - No console logs and no debug output (production-safe).
 */
(function () {
  const DATA_KEY = '__uspSiblingsCache';
  const LOTTIE_SPEED = 3.3;
  let lottieQueue = [];
  let lottieLoading = false;
  const OBSERVER_FLAG = '__uspObserverStarted';

  function ensureLottie(libUrl, cb) {
    if (window.lottie && typeof window.lottie.loadAnimation === 'function') {
      if (typeof cb === 'function') cb();
      return;
    }
    if (!libUrl) return;
    lottieQueue.push(cb);
    if (lottieLoading) return;
    lottieLoading = true;
    try {
      const s = document.createElement('script');
      s.src = libUrl;
      s.defer = true;
      s.onload = () => {
        lottieLoading = false;
        const q = lottieQueue.slice();
        lottieQueue = [];
        q.forEach((fn) => {
          if (typeof fn === 'function') fn();
        });
      };
      s.onerror = () => {
        lottieLoading = false;
        lottieQueue = [];
      };
      document.head.appendChild(s);
    } catch (_) {
      lottieLoading = false;
      lottieQueue = [];
    }
  }

  function parseJsonString(raw) {
    if (raw == null) return '';
    const s = String(raw);
    if (s === '') return '';
    try {
      // We store a JSON string (e.g. "\"Line1\\nLine2\"") in the attribute
      return JSON.parse(s);
    } catch (_) {
      // Fallback: treat as plain text
      return s;
    }
  }

  function normValue(s) {
    return String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '')
      .trim();
  }

  function getInfoTextEl(root) {
    try {
      return root.querySelector('.usp__info .usp__info-title');
    } catch (_) {
      return null;
    }
  }

  function setInfoText(tEl, next, defaultText) {
    if (!tEl) return;
    const safeNext = (next == null || String(next).trim() === '') ? (defaultText || '') : String(next);

    const apply = () => {
      tEl.textContent = safeNext;
      tEl.style.transition = 'opacity var(--anim-t-default) var(--easing-sharp)';
      tEl.style.opacity = '1';
    };

    // First-time update: avoid an empty flash by not forcing opacity to 0 before setting new text
    const isFirstSwap = (tEl.style.opacity === '' || tEl.style.opacity == null) &&
      (String(tEl.textContent || '').trim() === String(defaultText || '').trim());

    if (isFirstSwap) {
      apply();
      return;
    }

    tEl.style.opacity = '0';
    requestAnimationFrame(apply);
  }

  function getSiblingsData(root) {
    if (!root) return [];
    if (root[DATA_KEY]) return root[DATA_KEY];

    let data = [];
    try {
      const script = root.querySelector('script[type="application/json"][data-usp-siblings]');
      if (script) data = JSON.parse(script.textContent || '[]');
    } catch (_) {
      data = [];
    }

    root[DATA_KEY] = data;
    return data;
  }

  function getCurrentVariantOptions() {
    try {
      const el = document.querySelector('variant-selects [data-selected-variant]');
      if (!el) return null;
      const v = JSON.parse(el.textContent || 'null');
      if (!v || !Array.isArray(v.options)) return null;
      return v.options;
    } catch (_) {
      return null;
    }
  }

  function getLinkOptionIndexes(root) {
    // 0-based option positions to preserve when navigating to siblings.
    // Defaults to [0,1,2] when not provided (or for other navigation modes like collection).
    try {
      if (!root) return [0, 1, 2];
      const raw = root.getAttribute('data-link-opt-indexes');
      if (!raw) return [0, 1, 2];
      const parsed = JSON.parse(String(raw));
      if (!Array.isArray(parsed)) return [0, 1, 2];
      const out = parsed
        .map((n) => Number(n))
        .filter((n) => n === 0 || n === 1 || n === 2);
      return out;
    } catch (_) {
      return [0, 1, 2];
    }
  }

  function isNavigationMode(mode) {
    return mode === 'siblings' || mode === 'collection';
  }

  function onHover(e) {
    try {
      const btn = e.currentTarget;
      const root = btn.closest('fieldset[data-usp-mode]');
      if (!root) return;
      if (!isNavigationMode(root.getAttribute('data-usp-mode'))) return;

      const tEl = getInfoTextEl(root);
      if (!tEl) return; // no info row rendered

      const defaultText = parseJsonString(root.getAttribute('data-info-default'));
      const nextText = parseJsonString(btn.getAttribute('data-usp-info'));
      setInfoText(tEl, nextText, defaultText);
    } catch (_) {}
  }

  function onLeave(e) {
    try {
      const root = e.currentTarget.closest('fieldset[data-usp-mode]');
      if (!root) return;
      if (!isNavigationMode(root.getAttribute('data-usp-mode'))) return;

      const tEl = getInfoTextEl(root);
      if (!tEl) return;

      const defaultText = parseJsonString(root.getAttribute('data-info-default'));
      setInfoText(tEl, defaultText, defaultText);
    } catch (_) {}
  }

  function onNavigationClick(e) {
    try {
      const btn = e.currentTarget;
      const root = btn.closest('fieldset[data-usp-mode]');
      const mode = root ? root.getAttribute('data-usp-mode') : '';
      if (!root || !isNavigationMode(mode)) return;

      const pid = btn.getAttribute('data-usp-sibling');
      if (!pid) return;

      const siblings = getSiblingsData(root);
      const sib = siblings.find((s) => String(s.id) === String(pid));
      if (!sib || !sib.url) return;

      const destUrl = String(sib.url);
      const curOptions = getCurrentVariantOptions();
      const linkIdxs = getLinkOptionIndexes(root);

      // If we can't read current variant, go to sibling default variant (no param)
      if (!Array.isArray(curOptions) || curOptions.length === 0) {
        window.location.href = destUrl;
        return;
      }

      // If no option positions are configured for preservation, navigate to sibling default variant.
      if (!Array.isArray(linkIdxs) || linkIdxs.length === 0) {
        window.location.href = destUrl;
        return;
      }

      const curNorm = [curOptions[0], curOptions[1], curOptions[2]].map(normValue);

      let exact = null;
      let best = null;
      let bestMatches = -1;
      let bestShared = -1;
      let bestAvail = -1;
      if (Array.isArray(sib.variants)) {
        for (const v of sib.variants) {
          if (!v || !v.id) continue;
          const opts = Array.isArray(v.options) ? v.options : [];
          const optNorm = [opts[0], opts[1], opts[2]].map(normValue);

          let shared = 0;
          let matches = 0;
          for (const i of linkIdxs) {
            const a = curNorm[i];
            const b = optNorm[i];
            if (!a || !b) continue;
            shared++;
            if (a === b) matches++;
          }

          // Exact match for all comparable options
          if (shared > 0 && matches === shared) {
            exact = v;
            break;
          }

          // Best-match fallback: preserve as many options as possible
          // - Require at least one matching option
          // - Prefer higher matches; tie-break by higher shared (more comparable options)
          if (shared > 0 && matches > 0) {
            const availScore = v.available === true ? 1 : 0;
            if (
              matches > bestMatches ||
              (matches === bestMatches && shared > bestShared) ||
              (matches === bestMatches && shared === bestShared && availScore > bestAvail)
            ) {
              best = v;
              bestMatches = matches;
              bestShared = shared;
              bestAvail = availScore;
            }
          }
        }
      }

      // Exact match exists (even if sold out): navigate to that exact variant
      if (exact && exact.id) {
        window.location.href = destUrl + '?variant=' + String(exact.id);
        return;
      }

      // Best-match exists: preserve as much as possible
      if (best && best.id) {
        window.location.href = destUrl + '?variant=' + String(best.id);
        return;
      }

      // No match: navigate to destination default variant
      window.location.href = destUrl;
    } catch (_) {}
  }

  function init(rootNode) {
    const scope = rootNode || document;
    try {
      scope.querySelectorAll('fieldset[data-usp-mode]').forEach((root) => {
        if (root.dataset.uspInit === '1') return;
        root.dataset.uspInit = '1';

        const mode = root.getAttribute('data-usp-mode') || '';

        if (root.getAttribute('data-usp-dropdown') === 'true') {
          initVariantDropdown(root);
        }

        root.querySelectorAll('.usp__btn').forEach((btn) => {
          btn.addEventListener('pointerenter', onHover);
          btn.addEventListener('mouseenter', onHover);
          btn.addEventListener('focus', onHover);
          btn.addEventListener('pointerleave', onLeave);
          btn.addEventListener('mouseleave', onLeave);
          btn.addEventListener('blur', onLeave);

          if (isNavigationMode(mode)) btn.addEventListener('click', onNavigationClick);
        });

        // If you move the mouse out of the buttons container, revert the info row.
        if (isNavigationMode(mode)) {
          const cont = root.querySelector('[data-usp-buttons]');
          if (cont) {
            cont.addEventListener('mouseleave', () => {
              const anyBtn = cont.querySelector('.usp__btn');
              if (anyBtn) onLeave({ currentTarget: anyBtn });
            });
          }
        }
      });
    } catch (_) {}
  }

  // Dawn replaces parts of the product form on variant change.
  // That can recreate our dropdown markup, so we must re-initialize on DOM updates.
  function startObserver() {
    try {
      if (window[OBSERVER_FLAG]) return;
      window[OBSERVER_FLAG] = true;

      const obs = new MutationObserver((mutations) => {
        try {
          for (const m of mutations) {
            if (!m.addedNodes || m.addedNodes.length === 0) continue;
            for (const n of m.addedNodes) {
              if (!n || n.nodeType !== 1) continue; // elements only
              const el = /** @type {Element} */ (n);
              if (el.matches && el.matches('fieldset[data-usp-mode]')) {
                init(el);
                continue;
              }
              if (el.querySelector && el.querySelector('fieldset[data-usp-mode]')) {
                init(el);
              }
            }
          }
        } catch (_) {}
      });

      obs.observe(document.documentElement, { childList: true, subtree: true });
    } catch (_) {}
  }

  function initVariantDropdown(root) {
    try {
      if (!root || root.dataset.uspDropdownInit === '1') return;
      root.dataset.uspDropdownInit = '1';

      const trigger = root.querySelector('[data-usp-dropdown-trigger]');
      const overlay = root.querySelector('[data-usp-dropdown-overlay]');
      const panel = root.querySelector('[data-usp-dropdown-panel]');
      const valueEl = root.querySelector('[data-usp-dropdown-value]');
      const iconEl = root.querySelector('[data-usp-dropdown-icon]');

      if (!trigger || !overlay || !panel || !valueEl) return;

      const lottiePath = root.getAttribute('data-usp-lottie') || '';
      const lottieLib = root.getAttribute('data-usp-lottie-lib') || '';
      let anim = null;
      let pendingDir = null;

      if (iconEl) {
        iconEl.setAttribute('data-lottie-loading', 'true');
      }

      function refreshTriggerText() {
        const checked = root.querySelector('input[type="radio"]:checked');
        if (checked && valueEl) {
          valueEl.textContent = checked.value || '';
        }
      }

      function loadAnim() {
        if (!iconEl || !lottiePath) return;
        if (iconEl.dataset.lottieInit === '1') return;
        ensureLottie(lottieLib, () => {
          if (!window.lottie || iconEl.dataset.lottieInit === '1') return;
          try {
            anim = window.lottie.loadAnimation({
              container: iconEl,
              renderer: 'svg',
              loop: false,
              autoplay: false,
              path: lottiePath
            });
            iconEl.dataset.lottieInit = '1';
            anim.addEventListener('DOMLoaded', () => {
              iconEl.setAttribute('data-lottie-ready', 'true');
              iconEl.removeAttribute('data-lottie-loading');
              if (pendingDir != null) {
                anim.setSpeed(LOTTIE_SPEED);
                anim.setDirection(pendingDir);
                anim.play();
                pendingDir = null;
              }
            });
          } catch (_) {}
        });
      }

      function playAnim(dir) {
        if (anim) {
          anim.setSpeed(LOTTIE_SPEED);
          anim.setDirection(dir);
          anim.play();
          return;
        }
        pendingDir = dir;
        loadAnim();
      }

      function openDropdown() {
        root.classList.add('usp--dropdown-open');
        trigger.setAttribute('aria-expanded', 'true');
        overlay.setAttribute('aria-hidden', 'false');
        playAnim(1);
      }

      function closeDropdown() {
        root.classList.remove('usp--dropdown-open');
        trigger.setAttribute('aria-expanded', 'false');
        overlay.setAttribute('aria-hidden', 'true');
        playAnim(-1);
        refreshTriggerText();
      }

      trigger.addEventListener('click', () => {
        if (root.classList.contains('usp--dropdown-open')) {
          closeDropdown();
        } else {
          openDropdown();
        }
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeDropdown();
      });

      panel.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-usp-dropdown-option]');
        if (btn) {
          closeDropdown();
          return;
        }
        e.stopPropagation();
      });

      root.addEventListener('change', (e) => {
        if (e && e.target && e.target.matches('input[type="radio"]')) {
          refreshTriggerText();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && root.classList.contains('usp--dropdown-open')) {
          closeDropdown();
        }
      });

      refreshTriggerText();
      loadAnim();
    } catch (_) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      init();
      startObserver();
    });
  } else {
    init();
    startObserver();
  }
})();

